import * as Sentry from '@sentry/react';
import { Button, FieldError, Textarea, Typography } from '@eye-able/ui';
import { useAtomValue } from 'jotai';
import { type FormEvent, useState } from 'react';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { userAtom } from './global-store';

export default function ErrorFeedbackForm() {
  const { t } = useInlineTranslation();
  const user = useAtomValue(userAtom);
  const [additionalInformation, setAdditionalInformation] = useState('');
  const [validationMessage, setValidationMessage] = useState('');

  const sendAdditionalInformation = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const message = additionalInformation.trim();
    if (!message) {
      setValidationMessage(t('additionalInformationMissing'));
      return;
    }

    Sentry.captureFeedback({
      name: user?.fullName || 'No name',
      email: user?.email || 'No email',
      message,
      associatedEventId: Sentry.lastEventId() || 'User feedback',
    });
    setAdditionalInformation('');
    setValidationMessage('');
  };

  return (
    <form
      className='eye:flex eye:flex-col eye:gap-2xs'
      onSubmit={sendAdditionalInformation}
    >
      <Typography variant='body-m'>
        <T k='provideAdditionalErrorInformation' />
      </Typography>
      <Textarea
        name='additionalInfo'
        id='additionalInfo'
        rows={5}
        value={additionalInformation}
        onChange={(event) => {
          setAdditionalInformation(event.target.value);
          setValidationMessage('');
        }}
        aria-invalid={validationMessage ? 'true' : undefined}
        aria-describedby={
          validationMessage ? 'additional-information-error' : undefined
        }
        aria-label={t('additionalInformation')}
        placeholder={t('additionalInformation')}
      />
      {validationMessage && (
        <FieldError id='additional-information-error' role='alert'>
          {validationMessage}
        </FieldError>
      )}
      <Button type='submit' className='eye:self-end'>
        {t('send')}
      </Button>
    </form>
  );
}
